<template>
  <div ref="referenceRef" class="popper">
    <slot
      :open="open"
      :close="close"
      :isOpen="value"
      :toggle="toggle"
    />
    <Teleport :disabled="!teleportTo" :to="teleportTo">
      <div ref="contentRef" class="content" :class="{ open: value, hover }">
        <slot name="content" :close="close" />
        <div data-popper-arrow="true" class="arrow" />
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { createPopper, type Options, type Instance } from '@popperjs/core';
import { useVModel, onClickOutside, onKeyStroke } from '@vueuse/core';

interface Props {
  open?: boolean;
  hover?: boolean;
  closeOnClickOutside?: boolean;
  closeOnEsc?: boolean;
  teleportTo?: string;
  placement?: Options['placement'];
}

const emit = defineEmits(['update:open']);
const props = withDefaults(defineProps<Props>(), { placement: 'bottom' });
const value = useVModel(props, 'open', emit, { passive: true });

const open = () => {
  // eslint-disable-next-line no-use-before-define
  popper.value?.update();
  value.value = true;
};

const close = () => {
  value.value = false;
};

const toggle = () => {
  if (value.value) close();
  else open();
};

function usePopper(options?: Partial<Options>) {
  const referenceRef = ref<HTMLElement | null>(null);
  const contentRef = ref<HTMLElement | null>(null);
  const popper = ref<Instance>();

  onMounted(() => {
    if (referenceRef.value && contentRef.value) {
      popper.value = createPopper(referenceRef.value, contentRef.value, {
        ...options,
        modifiers: [
          { name: 'arrow', options: { padding: 20 } },
          {
            name: 'offset',
            options: {
              offset: [0, 10],
            },
          },
        ],
      });
    }
  });

  onUnmounted(() => {
    popper.value?.destroy();
  });

  return {
    referenceRef,
    contentRef,
    popper,
  };
}

const { referenceRef, contentRef, popper } = usePopper({
  placement: props.placement,
});

onClickOutside(contentRef, () => {
  if (props.closeOnClickOutside) {
    value.value = false;
  }
});

onKeyStroke('Escape', () => {
  if (props.closeOnEsc) {
    value.value = false;
  }
});
</script>

<style lang="scss" scoped>
.popper {
  @apply relative w-fit;
}

.content {
  @apply bg-white;
  @apply border border-secondary;
  @apply rounded-4;
  opacity: 0;
  transition: opacity var(--speed-fast) ease;
  z-index: 100;
  pointer-events: none;

  &.open,
  &.hover:hover {
    pointer-events: all;
    opacity: 1;
    transform: none;
  }
}

.arrow {
  z-index: -1;
}

.arrow,
.arrow::before {
  @apply absolute;
  --offset: 1rem;
  @apply border-secondary;
  width: var(--offset);
  height: var(--offset);
  background: inherit;
}

.arrow {
  visibility: hidden;
}

.arrow::before {
  visibility: visible;
  content: "";
  transform: rotate(45deg);
}

[data-popper-placement^="top"] > .arrow {
  &::before {
    @apply border-r border-b;
  }
  bottom: calc(var(--offset) * -0.5 - 2px);
}

[data-popper-placement^="bottom"] > .arrow {
  &::before {
    @apply border-l border-t;
  }
  top: calc(var(--offset) * -0.5 - 2px);
}

[data-popper-placement^="left"] > .arrow {
  &::before {
    @apply border-l border-b;
  }
  right: calc(var(--offset) * -0.5 - 2px);
}

[data-popper-placement^="right"] > .arrow {
  &::before {
    @apply border-r border-t;
  }
  left: calc(var(--offset) * -0.5 - 2px);
}
</style>
